import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import type { RootState } from '../../../store';
import {
  buildCompositionEditorRows,
  filterCompositionEditorRows,
  type CompositionEditorFilter,
} from '../lib/compositionEditorAdapter';
import { resolveCompositionEditGate } from '../lib/compositionEditGate';
import { attachCompositionRowActions } from '../lib/compositionRowActions';
import { userHasClosingPackageRole } from '../permissions/closingPackagePermissions';
import type { ClosingPackageCompositionResult, ClosingPackageProjection } from '../types';
import { useClosingPackagePermissions } from './useClosingPackagePermissions';

export type UseCompositionEditorOptions = {
  projection: ClosingPackageProjection;
  composition?: ClosingPackageCompositionResult | null;
};

/**
 * Single owner of editor state: edit gate, filter, derived rows.
 * Composition GET data is passed from Editor — no fetch in hook.
 */
export function useCompositionEditor({ projection, composition }: UseCompositionEditorOptions) {
  const [filter, setFilter] = useState<CompositionEditorFilter>('all');
  const permissions = useClosingPackagePermissions();
  const user = useSelector((state: RootState) => state.auth.user);

  const allowedToEdit = useMemo(
    () => permissions.canEdit && userHasClosingPackageRole(user, ['admin', 'accountant']),
    [permissions.canEdit, user]
  );

  const editGate = useMemo(
    () =>
      resolveCompositionEditGate({
        allowedToEdit,
        locked: projection.locked,
        formed: projection.formed,
        compositionMode: projection.compositionMode,
        workflowState: projection.workflowState,
      }),
    [
      allowedToEdit,
      projection.compositionMode,
      projection.formed,
      projection.locked,
      projection.workflowState,
    ]
  );

  const allRows = useMemo(
    () =>
      buildCompositionEditorRows({
        projection,
        composition: composition ?? null,
        canEdit: editGate.canEdit,
      }),
    [composition, editGate.canEdit, projection]
  );

  const rowsWithActions = useMemo(
    () => attachCompositionRowActions(allRows, editGate),
    [allRows, editGate]
  );

  const rows = useMemo(
    () => filterCompositionEditorRows(rowsWithActions, filter),
    [rowsWithActions, filter]
  );

  const emptyDescription =
    filter === 'all'
      ? 'Документы комплекта отсутствуют'
      : filter === 'excluded'
        ? 'Нет исключённых документов'
        : 'Нет документов по выбранному фильтру';

  const canInitialize = useMemo(
    () =>
      allowedToEdit &&
      projection.compositionMode === 'RECOMMENDED' &&
      !projection.locked &&
      !projection.formed &&
      (projection.workflowState === 'DRAFT' ||
        projection.workflowState === 'REVISION_REQUIRED'),
    [
      allowedToEdit,
      projection.compositionMode,
      projection.formed,
      projection.locked,
      projection.workflowState,
    ]
  );

  return {
    filter,
    setFilter,
    canEdit: editGate.canEdit,
    canInitialize,
    readOnlyReason: editGate.readOnlyReason,
    isLocked: editGate.isLocked,
    isFormed: editGate.isFormed,
    allRows,
    rows,
    emptyDescription,
  };
}

export type UseCompositionEditorResult = ReturnType<typeof useCompositionEditor>;
